import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge
} from 'react-bootstrap';
import { promptsAPI, tagsAPI } from '../services/api';
import toast from 'react-hot-toast';
// ... existing code ...
import { Save, XCircle, Tag as TagIcon, LayoutTemplate, Link as LinkIcon, Edit as EditIcon, Globe, Lock, Users, Plus } from 'lucide-react';

const EditPromptPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    description: '',
    visibility: 'private', // Default to private
    tags: [],
    category: '',
    aiModels: [],
    isTemplate: false,
    templateVariables: [],
  });
  // Removed initialTagsLoaded as it's not being used and can be removed for clarity.

  // Fetch existing prompt data
  const { data: promptData, isLoading: isLoadingPrompt, error: promptError } = useQuery(
    ['prompt', id],
    () => promptsAPI.getPrompt(id),
    {
      enabled: !!id, // Only run query if id is available
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Removed onSuccess here. The logic is moved to useEffect below.
    }
  );

  // Use useEffect to populate form data when promptData changes
  useEffect(() => {
    if (promptData?.data) {
      const prompt = promptData.data; // Access the 'data' property of the response
      setFormData({
        title: prompt.title || '',
        content: prompt.content || '',
        description: prompt.description || '',
        visibility: prompt.visibility || 'private',
        tags: prompt.tags?.map(tag => tag._id) || [], // Store only tag IDs
        category: prompt.category || '',
        aiModels: prompt.aiModels || [],
        isTemplate: prompt.isTemplate || false,
        templateVariables: prompt.templateVariables || [],
      });
    }
  }, [promptData]); // Depend on promptData so this runs whenever it changes

  // Fetch available tags
  const { data: availableTagsData, isLoading: isLoadingTags } = useQuery(
    ['tags', 'all'],
    () => tagsAPI.getTags({ limit: 100 }),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fetch categories
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery(
    ['prompts', 'categories'],
    () => promptsAPI.getCategories(),
    {
      staleTime: 10 * 60 * 1000,
    }
  );

  // Mutation for updating the prompt
  const updatePromptMutation = useMutation(
    (updatedPrompt) => promptsAPI.updatePrompt(id, updatedPrompt),
    {
      onSuccess: () => {
        toast.success('Prompt erfolgreich aktualisiert!');
        queryClient.invalidateQueries(['prompt', id]); // Invalidate to refetch fresh data
        queryClient.invalidateQueries('prompts'); // Invalidate all prompts list
        navigate(`/prompts/${id}`);
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Fehler beim Aktualisieren des Prompts');
      },
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'tags') {
      // Handle multiple select for tags
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData(prev => ({ ...prev, tags: selectedOptions }));
    } else if (name === 'aiModels') {
      // Handle multiple select for AI models
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData(prev => ({ ...prev, aiModels: selectedOptions }));
    }
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTemplateVariableChange = (index, field, value) => {
    const newTemplateVariables = [...formData.templateVariables];
    newTemplateVariables[index] = { ...newTemplateVariables[index], [field]: value };
    setFormData(prev => ({ ...prev, templateVariables: newTemplateVariables }));
  };

  const addTemplateVariable = () => {
    setFormData(prev => ({
      ...prev,
      templateVariables: [...prev.templateVariables, { name: '', description: '', type: 'text', defaultValue: '' }]
    }));
  };

  const removeTemplateVariable = (index) => {
    setFormData(prev => ({
      ...prev,
      templateVariables: prev.templateVariables.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updatePromptMutation.mutate(formData);
  };

  if (isLoadingPrompt || isLoadingTags || isLoadingCategories) {
    return (
      <Container fluid className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3">Editor wird geladen...</p>
      </Container>
    );
  }

  if (promptError) {
    return (
      <Container fluid>
        <Alert variant="danger">
          <Alert.Heading>Fehler beim Laden des Prompts</Alert.Heading>
          <p>{promptError.message}</p>
          <Button onClick={() => navigate('/prompts')}>Zurück zu den Prompts</Button>
        </Alert>
      </Container>
    );
  }

  // Helper to get tag display name
  const getTagDisplayName = (tagId) => {
    return availableTagsData?.find(tag => tag._id === tagId)?.displayName || tagId;
  };
  
  // Helper to get visibility icon
  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public':
        return <Globe size={16} className="text-success me-2" />;
      case 'team':
        return <Users size={16} className="text-info me-2" />;
      case 'private':
        return <Lock size={16} className="text-secondary me-2" />;
      default:
        return <Lock size={16} className="text-secondary me-2" />;
    }
  };


  const availableTags = availableTagsData || [];
  const categories = categoriesData || []; // categoriesData is directly the array

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h1 className="h3 mb-1">Prompt bearbeiten</h1>
          <p className="text-muted">Passe die Details deines Prompts an.</p>
        </Col>
      </Row>

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col lg={8}>
            <Card className="mb-4">
              <Card.Header>
                <h5>Prompt-Details</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group controlId="title" className="mb-3">
                  <Form.Label>Titel</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="description" className="mb-3">
                  <Form.Label>Beschreibung</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group controlId="content" className="mb-3">
                  <Form.Label>Inhalt</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={10}
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="visibility" className="mb-3">
                  <Form.Label>Sichtbarkeit</Form.Label>
                  <Form.Select
                    name="visibility"
                    value={formData.visibility}
                    onChange={handleChange}
                  >
                    <option value="private">Privat</option>
                    <option value="team">Team</option>
                    <option value="public">Öffentlich</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="category" className="mb-3">
                  <Form.Label>Kategorie</Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                  >
                    <option value="">Wähle eine Kategorie</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="tags" className="mb-3">
                  <Form.Label>Tags</Form.Label>
                  <Form.Control
                    as="select"
                    multiple
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                  >
                    {availableTags.map((tag) => (
                      <option key={tag._id} value={tag._id}>
                        {tag.displayName}
                      </option>
                    ))}
                  </Form.Control>
                  <div className="mt-2">
                    {formData.tags.map(tagId => (
                      <Badge key={tagId} bg="primary" className="me-1">
                        {getTagDisplayName(tagId)}
                      </Badge>
                    ))}
                  </div>
                </Form.Group>

                <Form.Group controlId="aiModels" className="mb-3">
                  <Form.Label>KI-Modelle</Form.Label>
                  <Form.Control
                    as="select"
                    multiple
                    name="aiModels"
                    value={formData.aiModels}
                    onChange={handleChange}
                  >
                    <option value="gpt-3.5-turbo">GPT-3.5</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="claude-2">Claude-2</option>
                    <option value="claude-3">Claude-3</option>
                    <option value="bard">Bard</option>
                    <option value="other">Andere</option>
                  </Form.Control>
                  <div className="mt-2">
                    {formData.aiModels.map(model => (
                      <Badge key={model} bg="secondary" className="me-1">{model}</Badge>
                    ))}
                  </div>
                </Form.Group>

                <Form.Group controlId="isTemplate" className="mb-3 form-check">
                  <Form.Check
                    type="checkbox"
                    label="Ist Vorlage?"
                    name="isTemplate"
                    checked={formData.isTemplate}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {formData.isTemplate && (
            <Col lg={4}>
              <Card className="mb-4">
                <Card.Header>
                  <h5>Vorlagenvariablen</h5>
                  <Button variant="outline-primary" size="sm" onClick={addTemplateVariable}>
                    <Plus size={16} /> Variable hinzufügen
                  </Button>
                </Card.Header>
                <Card.Body>
                  {formData.templateVariables.length === 0 && (
                    <Alert variant="info">Keine Vorlagenvariablen definiert.</Alert>
                  )}
                  {formData.templateVariables.map((variable, index) => (
                    <Card key={index} className="mb-3 p-2">
                      <Form.Group controlId={`varName-${index}`} className="mb-2">
                        <Form.Label className="small mb-0">Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={variable.name}
                          onChange={(e) => handleTemplateVariableChange(index, 'name', e.target.value)}
                          required
                          size="sm"
                        />
                      </Form.Group>
                      <Form.Group controlId={`varDesc-${index}`} className="mb-2">
                        <Form.Label className="small mb-0">Beschreibung</Form.Label>
                        <Form.Control
                          type="text"
                          value={variable.description}
                          onChange={(e) => handleTemplateVariableChange(index, 'description', e.target.value)}
                          size="sm"
                        />
                      </Form.Group>
                      <Form.Group controlId={`varType-${index}`} className="mb-2">
                        <Form.Label className="small mb-0">Typ</Form.Label>
                        <Form.Select
                          value={variable.type}
                          onChange={(e) => handleTemplateVariableChange(index, 'type', e.target.value)}
                          size="sm"
                        >
                          <option value="text">Text</option>
                          <option value="number">Zahl</option>
                          <option value="boolean">Boolean</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group controlId={`varDefault-${index}`} className="mb-2">
                        <Form.Label className="small mb-0">Standardwert</Form.Label>
                        <Form.Control
                          type="text"
                          value={variable.defaultValue}
                          onChange={(e) => handleTemplateVariableChange(index, 'defaultValue', e.target.value)}
                          size="sm"
                        />
                      </Form.Group>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeTemplateVariable(index)}
                        className="mt-2"
                      >
                        Entfernen
                      </Button>
                    </Card>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>

        <Row className="mt-4">
          <Col className="text-end">
            <Button variant="secondary" onClick={() => navigate(`/prompts/${id}`)} className="me-2">
              <XCircle size={16} className="me-2" />
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={updatePromptMutation.isLoading}>
              {updatePromptMutation.isLoading ? <Spinner animation="border" size="sm" className="me-2" /> : <Save size={16} className="me-2" />}
              Änderungen speichern
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default EditPromptPage;